import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Globe, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const LANGS = [
  { code: "it", label: "Italiano" },
  { code: "en", label: "English" },
];

export default function AdminPrivacyTemplate() {
  const { i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const [tpl, setTpl] = useState(null);
  const [activeLang, setActiveLang] = useState("it");
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(1);

  useEffect(() => {
    api.get("/admin/privacy-template").then(({ data }) => {
      setTpl({
        title_it: data.title_it,
        title_en: data.title_en,
        body_it: data.body_it,
        body_en: data.body_en,
      });
      setVersion(data.version || 1);
    });
  }, []);

  if (!tpl) return <div className="text-zinc-400">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/privacy-template", tpl);
      setVersion(data.version || version + 1);
      toast.success(isEN ? "Privacy notice saved" : "Informativa salvata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="privacy-template-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700]">{isEN ? "GDPR" : "GDPR"}</p>
          <h1 className="font-display text-4xl font-bold mt-1 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-[#FFD700]" />
            {isEN ? "Privacy notice" : "Informativa Privacy"}
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            {isEN
              ? "Edit the GDPR privacy notice presented at registration and checkout. Consents are versioned and audit-logged with IP and User-Agent."
              : "Modifica l'informativa privacy GDPR mostrata in registrazione e checkout. I consensi sono versionati con audit log di IP e User-Agent."}
          </p>
          <p className="text-xs text-zinc-600 mt-1">v.{version}</p>
        </div>
        <Button onClick={save} disabled={saving}
                className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold"
                data-testid="privacy-template-save">
          <Save className="w-4 h-4 mr-1" />
          {saving ? (isEN ? "Saving…" : "Salvataggio…") : (isEN ? "Save notice" : "Salva informativa")}
        </Button>
      </div>

      <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 flex items-start gap-3 text-sm">
        <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-zinc-300">
          {isEN
            ? "Changes apply only to future consents. Already-registered users keep their original snapshot for legal traceability."
            : "Le modifiche valgono solo per i consensi futuri. Gli utenti già registrati conservano la versione originale per tracciabilità legale."}
        </div>
      </div>

      <div className="flex gap-1 border-b border-zinc-800">
        {LANGS.map((l) => (
          <button
            key={l.code}
            onClick={() => setActiveLang(l.code)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              activeLang === l.code ? "border-[#FFD700] text-[#FFD700]" : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid={`privacy-tab-${l.code}`}
          >
            <Globe className="w-3.5 h-3.5 inline mr-1" />
            {l.label}
          </button>
        ))}
      </div>

      <div className="space-y-4 border border-zinc-800 rounded-xl p-5 bg-zinc-900/40">
        <div>
          <Label className="text-xs text-zinc-400">{isEN ? "Title" : "Titolo"}</Label>
          <Input
            value={tpl[`title_${activeLang}`]}
            onChange={(e) => setTpl({ ...tpl, [`title_${activeLang}`]: e.target.value })}
            className="bg-zinc-950 border-zinc-800 mt-1 font-semibold"
            data-testid="privacy-title-input"
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">{isEN ? "Notice body (plain text / markdown)" : "Testo informativa (testo libero / markdown)"}</Label>
          <Textarea
            rows={20}
            value={tpl[`body_${activeLang}`]}
            onChange={(e) => setTpl({ ...tpl, [`body_${activeLang}`]: e.target.value })}
            className="bg-zinc-950 border-zinc-800 mt-1 font-mono text-sm leading-relaxed"
            data-testid="privacy-body-input"
          />
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold shadow-lg shadow-[#FFD700]/20">
          <Save className="w-4 h-4 mr-1" />
          {saving ? (isEN ? "Saving…" : "Salvataggio…") : (isEN ? "Save changes" : "Salva modifiche")}
        </Button>
      </div>
    </div>
  );
}
